import { useRef, useState } from "react";
import { Download, FileUp, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

export default function ImportPage() {
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCsvText(reader.result); setPreview(null); setResult(null); };
    reader.readAsText(file, "utf-8");
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/products/preview", { csv_text: csvText });
      setPreview(data);
      setResult(null);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/products/commit", { csv_text: csvText, mode });
      setResult(data);
      toast.success(`İçe aktarma tamamlandı: ${data.created} yeni, ${data.updated} güncellendi`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doExport = async () => {
    try {
      const { data } = await api.get("/admin/export/products");
      const blob = new Blob(["\uFEFF" + data.csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "bigeldi-urunler.csv";
      a.click();
      toast.success(`${data.count} ürün dışa aktarıldı`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="max-w-5xl space-y-5" data-testid="admin-import">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">CSV İçe / Dışa Aktarım</h1>
        <button onClick={doExport} className="h-10 px-4 rounded-xl bg-[#181B20] border border-[#272B33] text-sm text-white font-semibold flex items-center gap-2 hover:border-blue-500" data-testid="export-products-btn">
          <Download size={15} /> Ürünleri CSV İndir
        </button>
      </div>

      <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 space-y-4">
        <p className="text-xs text-neutral-400 leading-relaxed">
          Desteklenen sütunlar (noktalı virgül veya virgül ile ayrılmış):{" "}
          <code className="font-mono-ed text-[11px] text-blue-400">name; sku; barcode; product_code; category; subcategory; brand; description; price; sale_price; vat_rate; stock; image_url</code>
          <br />Varyant satırları için: <code className="font-mono-ed text-[11px] text-blue-400">parent_sku; variant_values (Renk=Siyah,Beden=M); variant_stock; variant_price</code>
        </p>
        <div className="flex gap-2">
          <button onClick={() => fileRef.current?.click()} className="h-11 px-5 rounded-xl bg-[#0F1115] border border-dashed border-[#272B33] text-sm text-neutral-300 flex items-center gap-2 hover:border-blue-500" data-testid="import-file-btn">
            <FileUp size={16} /> CSV Dosyası Seç
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
        </div>
        <textarea rows={8} value={csvText} onChange={(e) => { setCsvText(e.target.value); setPreview(null); setResult(null); }}
          placeholder={"name;sku;category;brand;price;stock\nÖrnek Ürün;SKU-001;Giyim;Bigeldi;499.90;10"}
          className="w-full px-4 py-3 bg-[#0F1115] border border-[#272B33] rounded-xl text-xs font-mono-ed text-white focus:outline-none focus:border-blue-500 resize-y"
          data-testid="import-csv-textarea" />
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={doPreview} disabled={!csvText.trim() || busy} className="h-11 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-40" data-testid="import-preview-btn">
            Önizleme
          </button>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-11 px-4 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white" data-testid="import-mode">
            <option value="upsert">Varsa güncelle, yoksa oluştur</option>
            <option value="create_only">Sadece yeni ürün oluştur</option>
          </select>
          {preview && preview.row_count > 0 && (
            <button onClick={doCommit} disabled={busy} className="h-11 px-6 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-40 flex items-center gap-2" data-testid="import-commit-btn">
              <Upload size={15} /> İçe Aktar ({preview.row_count} satır)
            </button>
          )}
        </div>
      </section>

      {preview && (
        <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6" data-testid="import-preview">
          <h2 className="font-semibold text-white text-sm mb-4">Önizleme — {preview.row_count} geçerli satır, {preview.errors.length} hata</h2>
          {preview.errors.length > 0 && (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 mb-4 space-y-1">
              {preview.errors.map((e, i) => <p key={i} className="text-xs text-rose-400">Satır {e.line}: {e.error}</p>)}
            </div>
          )}
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead><tr className="text-left text-neutral-500 border-b border-[#272B33]">
                <th className="px-3 py-2">Satır</th><th className="px-3 py-2">Ürün</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Kategori</th><th className="px-3 py-2">Fiyat</th><th className="px-3 py-2">Stok</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#272B33]/40">
                    <td className="px-3 py-2 text-neutral-500">{r._line}</td>
                    <td className="px-3 py-2 text-white">{r.name}{r.parent_sku ? <span className="text-amber-400"> (varyant → {r.parent_sku})</span> : null}</td>
                    <td className="px-3 py-2 font-mono-ed text-neutral-400">{r.sku || "—"}</td>
                    <td className="px-3 py-2 text-neutral-400">{r.category || "—"}</td>
                    <td className="px-3 py-2 text-neutral-300">{r.price ?? r.variant_price ?? "—"}</td>
                    <td className="px-3 py-2 text-neutral-300">{r.stock ?? r.variant_stock ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {result && (
        <section className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6" data-testid="import-result">
          <p className="text-sm text-emerald-400 font-semibold">İçe aktarma tamamlandı</p>
          <p className="text-xs text-neutral-400 mt-1">{result.created} ürün oluşturuldu · {result.updated} güncellendi · {result.skipped} atlandı · {result.errors.length} hata</p>
        </section>
      )}
    </div>
  );
}
